import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchoolsEntryScreen } from './hooks/useSchoolsEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({ label, value, onChange, placeholder, required = false }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
      />
    </label>
  );
}

export default function SchoolsEntryScreen() {
  const { state, actions } = useSchoolsEntryScreen();

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  const completionPercent = state.form.name.trim() ? 100 : 0;

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Top Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Organization</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <Link to="/masters/school-college/list" className="hover:text-primary transition-colors">
              School / College Registry
            </Link>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">{state.isEditMode ? 'Edit' : 'Create'}</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit School / College' : 'Create School / College'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure academic institution name.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/school-college/list"
            className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-5 py-2.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-highest cursor-pointer"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.isSaving ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{state.isEditMode ? 'Update School/College' : 'Save School/College'}</span>
            )}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="mt-3 text-xs font-medium text-on-surface-variant">Loading school details...</span>
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          {/* Main Left Column */}
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-lg font-bold text-on-surface">General Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div>
                  <TextField
                    label="School / College Name"
                    required
                    value={state.form.name}
                    onChange={(val) => actions.patchForm('name', val)}
                    placeholder="e.g. Siddharth Law College"
                  />
                </div>
              </div>
            </article>
          </div>

          {/* Aside Right Column */}
          <div className="col-span-12 space-y-6 lg:col-span-4">
            {/* Operational Status Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-base font-bold text-on-surface">Operational Status</h2>
              </div>
              <p className="text-xs text-on-surface-variant leading-relaxed mb-4">
                Active institutions are available for immediate selection during student admission and qualifying school assignment.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('active')}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-4 text-xs font-bold transition cursor-pointer ${
                    state.operationalStatus === 'active'
                      ? 'border-primary bg-primary/10 text-primary shadow-xs'
                      : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl">check_circle</span>
                  <span>Active</span>
                </button>

                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('inactive')}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-4 text-xs font-bold transition cursor-pointer ${
                    state.operationalStatus === 'inactive'
                      ? 'border-outline bg-surface-container-high text-on-surface shadow-xs'
                      : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl">archive</span>
                  <span>Archived</span>
                </button>
              </div>
            </article>

            {/* Configuration Completion Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-base font-bold text-on-surface">Configuration Completion</h2>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
                  <svg className="h-14 w-14 -rotate-90" viewBox="0 0 36 36">
                    <path
                      className="text-surface-container-high"
                      strokeWidth="3.5"
                      stroke="currentColor"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="text-primary transition-all duration-300"
                      strokeDasharray={`${completionPercent}, 100`}
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <span className="absolute text-xs font-bold text-on-surface">
                    {completionPercent}%
                  </span>
                </div>
                <div>
                  <p className="text-xs font-bold text-on-surface">Profile Readiness</p>
                  <p className="text-[11px] text-on-surface-variant">
                    {completionPercent === 100
                      ? 'All details complete.'
                      : 'Provide institution name to save.'}
                  </p>
                </div>
              </div>

              <div className="space-y-2 border-t border-outline-variant/15 pt-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">Institution Name:</span>
                  <strong className="text-on-surface font-semibold">
                    {state.form.name.trim() ? 'Configured' : 'Missing'}
                  </strong>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  );

}
